import NiceModal from '@ebay/nice-modal-react';
import { css } from '@emotion/react';

import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import Alert from 'components/global/Alert';
import Image from 'components/global/Image';
import Typography from 'components/global/Typography';
import { ModalActions } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import fetchRoles from 'services/common/fetchRoles';
import fetchUserDetails from 'services/common/fetchUserDetails';
import { PAYMENT } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import { getManageSubscriptionData, setManageSubscriptionReload } from '../SubscriptionAccountSlice';
import * as S from '../SubscriptonAccountModal.styled';
import DowngradeReasonModal from './DowngradeReasonModal';
import MonthlyFreeIntegratorShipments from './MonthlyFreeIntegratorShipments';

export default function DowngradeConfirmationModal({
	closeModal,
	prevStep,
	selectedPackage,
	currentPackage,
	planType
}) {
	// Hooks and states declaration
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const axios = useAxiosPrivate();
	const { Currency, getDate } = useLocale();
	const { showSuccess, showError } = useAlert();
	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);

	const user = useSelector((state) => state.user);
	const { manageSubscriptionData } = useSelector((state) => state.subscriptionAccount);

	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		dispatch(getManageSubscriptionData());
	}, []);

	const downgradeButton = async () => {
		setIsLoading(true);

		try {
			const response = await axios.post(PAYMENT.CREATE_SUBSCRIPTION, {
				oldPackageID: !user.accountSuspended ? currentPackage.tier : undefined,
				newPackageID: selectedPackage?.tier,
				paymentType: selectedPackage?.tier === 1 ? undefined : planType.toUpperCase(),
				currency: selectedPackage?.tier === 1 ? undefined : Currency
			});
			if (response.status === 200) {
				showSuccess();
				closeModal();
				fetchUserDetails();
				fetchRoles();
				dispatch(setManageSubscriptionReload(true));
				NiceModal.show(DowngradeReasonModal);
			} else {
				AppLogger.error(response);
				setIsLoading(false);
			}
		} catch (error) {
			showError({
				message: error?.response?.data?.code || error?.response?.data?.message
			});
			AppLogger.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<S.ModalContentWrapper>
				<S.StyledForm className="mt-2">
					<div>
						<S.ImageContainer>
							<Image width="11rem" height="11rem" url={selectedPackage.packageIcon} />
						</S.ImageContainer>

						{!user.accountSuspended && (
							<S.TextCenterContainer className="mb-5 mt-3">
								<Typography fontWeight="500" variant="p2" className="mb-3">
									{t('downgradeTitle', {
										currentPackageName: t(`${currentPackage.planName}.packageTitle`),
										selectedPackageName: t(`${selectedPackage.planName}.packageTitle`)
									})}
								</Typography>
								<Typography variant="p3" color="var(--content-2)" noWrap>
									<Trans
										i18nKey="downgradePlanSubtitle"
										values={{
											currentPackageName: t(`${currentPackage.planName}.packageTitle`),
											selectedPackageName: t(`${selectedPackage.planName}.packageTitle`)
										}}
										components={{
											b: <strong />
										}}
									/>
								</Typography>
							</S.TextCenterContainer>
						)}

						{user.accountSuspended && (
							<S.TextCenterContainer className="mb-5 mt-3">
								<Typography fontWeight="500" variant="p2" className="mb-3">
									{t('upgradeTitle', {
										currentPackageName: t(`${currentPackage.planName}.packageTitle`),
										selectedPackageName: t(`${selectedPackage.planName}.packageTitle`)
									})}
								</Typography>
								<Typography variant="p3" color="var(--content-4)" noWrap>
									<Trans
										i18nKey="upgradePlanSubtitle"
										values={{
											currentPackageName: t(`${currentPackage.planName}.packageTitle`),
											selectedPackageName: t(`${selectedPackage.planName}.packageTitle`)
										}}
										components={{
											b: <strong />
										}}
									/>
								</Typography>
							</S.TextCenterContainer>
						)}
						<S.ConfirmationDataBox>
							<div>
								<Typography fontWeight="500" color="var(--content-3)" css={{ width: '150px' }}>
									currentPackage
								</Typography>

								<div>
									<Typography color="var(--content-2)">
										{t(`${currentPackage.planName}.packageTitle`)}
										{!user.accountSuspended && (
											<>
												-
												<b>
													{planType === 'yearly'
														? currentPackage.yearlyPrice
														: currentPackage.monthlyPrice}{' '}
													{currentPackage.currency}
												</b>
												/{planType === 'yearly' ? t('year') : t('month')}
											</>
										)}
									</Typography>
									{currentPackage.monthlyFreeIntegratorOrder && (
										<MonthlyFreeIntegratorShipments pricingPackage={currentPackage} />
									)}
								</div>
							</div>
							<div>
								<Typography fontWeight="500" color="var(--content-3)" css={{ width: '150px' }}>
									newPackage
								</Typography>
								<div>
									<Typography>
										{t(`${selectedPackage.planName}.packageTitle`)} -{' '}
										<b>
											{planType === 'yearly'
												? selectedPackage?.yearlyPrice
												: selectedPackage?.monthlyPrice}{' '}
											{selectedPackage.currency}
										</b>
										/{planType === 'yearly' ? t('year') : t('month')}
									</Typography>
									{selectedPackage.monthlyFreeIntegratorOrder && (
										<MonthlyFreeIntegratorShipments pricingPackage={selectedPackage} />
									)}
								</div>
							</div>
						</S.ConfirmationDataBox>
						{!user.accountSuspended && (
							<Alert
								className="mt-4 mb-2"
								isMobile={isMobile}
								content={t('downgradeInfoText', {
									startDate: getDate(manageSubscriptionData?.startDate),
									endDate: getDate(manageSubscriptionData?.nextBillingDate),
									currentPackageName: t(`${currentPackage.planName}.packageTitle`),
									selectedPackageName: t(`${selectedPackage.planName}.packageTitle`)
								})}
								showIcon
							/>
						)}
					</div>
				</S.StyledForm>
			</S.ModalContentWrapper>
			<ModalActions className="subscription-modal-actions">
				<Button
					label="cancel"
					variant="blackOutline"
					css={css`
						text-decoration: underline;
						border: none !important;
					`}
					onClick={closeModal}
				/>
				<Button
					variant="outline"
					label="previous"
					onClick={() => {
						prevStep();
					}}
				/>

				<Button
					variant="blue"
					loading={isLoading}
					label="save"
					onClick={() => {
						downgradeButton();
					}}
				/>
			</ModalActions>
		</>
	);
}
